import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { Eye, EyeOff, X } from 'lucide-react';
import { useToast } from '../context/ToastContext';

interface AddCompassModalProps {
  memberId: string;
  memberName: string;
  onClose: () => void;
}

export function AddCompassModal({ memberId, memberName, onClose }: AddCompassModalProps) {
  const [schoolDomain, setSchoolDomain] = useState('mountalexandercollege-vic.compass.education');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const queryClient = useQueryClient();
  const toast = useToast();

  const connectMutation = useMutation({
    mutationFn: async () => {
      // 1. Connect Compass account
      const connectRes = (await api.post('/auth/compass/connect', {
        schoolDomain,
        username,
        password,
        memberId
      })) as any;

      // 2. Create calendar source
      // api.post unwraps response.data, so connectRes is { connectedAccountId: '...' }
      await api.post('/sources', {
        type: 'compass',
        memberId,
        connectedAccountId: connectRes.connectedAccountId,
        name: `${memberName}'s School Calendar`,
        refreshInterval: 86400 // Daily
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarSources'] });
      queryClient.invalidateQueries({ queryKey: ['connectedAccounts'] });
      toast.success(`Connected ${memberName}'s Compass calendar`);
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to connect Compass calendar';
      toast.error(message);
    }
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-secondary hover:text-text-primary"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-semibold mb-4">Connect Compass Education</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              School Domain
            </label>
            <input
              type="text"
              placeholder="e.g., school-name-vic.compass.education"
              value={schoolDomain}
              onChange={(e) => setSchoolDomain(e.target.value)}
              className="w-full px-3 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
            />
            <p className="text-xs text-text-secondary mt-1">
              Find this in your school's Compass URL
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Compass Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Compass Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-900">
              🔒 Your password is encrypted with AES-256-GCM before storage and never stored in plain text.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border-default rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => connectMutation.mutate()}
              disabled={connectMutation.isPending || !schoolDomain || !username || !password}
              className="flex-1 px-4 py-2 bg-soft-cyan text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {connectMutation.isPending ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
